import { useState, useMemo } from "react";
import { calculateRate } from "../../../data/courierData";
import { useCouriers, CourierProvider } from "../../../context/CourierContext";
import {
  Box, Card, CardContent, Typography, TextField, Button, Paper,
  FormControl, InputLabel, Select, MenuItem, Grid, Divider,
} from "@mui/material";
import CalculateIcon from "@mui/icons-material/Calculate";
import CurrencyRupeeIcon from "@mui/icons-material/CurrencyRupee";
import LocalShippingIcon from "@mui/icons-material/LocalShipping";

const RatecardList = () => {
  const [courierId, setCourierId] = useState("");
  const [serviceId, setServiceId] = useState("");
  const [rateCardId, setRateCardId] = useState("");
  const [weight, setWeight] = useState("");
  const [result, setResult] = useState(null);

  const { couriers } = useCouriers();
  const activeCouriers = couriers.filter((c) => c.status === "Active");

  const services = useMemo(
    () => activeCouriers.find((c) => c.id === courierId)?.services || [],
    [courierId, activeCouriers]
  );

  const rateCards = useMemo(
    () => services.find((s) => s.id === serviceId)?.rateCards || [],
    [serviceId, services]
  );

  const handleCourierChange = (val) => { setCourierId(val); setServiceId(""); setRateCardId(""); setResult(null); };
  const handleServiceChange = (val) => { setServiceId(val); setRateCardId(""); setResult(null); };

  const handleCalculate = () => {
    if (!courierId || !serviceId || !rateCardId || !weight) return;
    setResult(calculateRate(courierId, serviceId, rateCardId, parseFloat(weight), couriers));
  };

  return (
    <Box sx={{ minHeight: "100vh", bgcolor: "background.default" }}>
      <Box sx={{ maxWidth: 1200, mx: "auto", px: 3, py: 4 }}>
        <Typography variant="h5" gutterBottom>Calculate Rate</Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          Enter shipment details to get the estimated rate
        </Typography>

        <Grid container spacing={3}>
          {/* Form */}
          <Grid size={{ xs: 12, md: 6 }}>
            <Card>
              <CardContent>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
                  <CalculateIcon color="primary" />
                  <Typography variant="h6" fontSize={16}>Shipment Details</Typography>
                </Box>
                <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                  <FormControl size="small" fullWidth>
                    <InputLabel>Courier</InputLabel>
                    <Select value={courierId} label="Courier" onChange={(e) => handleCourierChange(e.target.value)}>
                      {activeCouriers.map((c) => <MenuItem key={c.id} value={c.id}>{c.name}</MenuItem>)}
                    </Select>
                  </FormControl>

                  <FormControl size="small" fullWidth disabled={!courierId}>
                    <InputLabel>Service</InputLabel>
                    <Select value={serviceId} label="Service" onChange={(e) => handleServiceChange(e.target.value)}>
                      {services.map((s) => <MenuItem key={s.id} value={s.id}>{s.name} ({s.mode} · {s.deliveryDays})</MenuItem>)}
                    </Select>
                  </FormControl>

                  <FormControl size="small" fullWidth disabled={!serviceId}>
                    <InputLabel>Rate Card / Zone</InputLabel>
                    <Select value={rateCardId} label="Rate Card / Zone" onChange={(e) => { setRateCardId(e.target.value); setResult(null); }}>
                      {rateCards.map((rc) => <MenuItem key={rc.id} value={rc.id}>{rc.name} — {rc.zone}</MenuItem>)}
                    </Select>
                  </FormControl>

                  <TextField label="Weight (kg)" type="number" size="small" fullWidth placeholder="e.g. 2.5"
                    value={weight} onChange={(e) => { setWeight(e.target.value); setResult(null); }}
                    inputProps={{ min: 0.1, step: 0.1 }} />

                  <Button variant="contained" fullWidth startIcon={<CalculateIcon />} onClick={handleCalculate}
                    disabled={!courierId || !serviceId || !rateCardId || !weight}>
                    Calculate Rate
                  </Button>
                </Box>
              </CardContent>
            </Card>
          </Grid>

          {/* Result */}
          <Grid size={{ xs: 12, md: 6 }}>
            {result ? (
              <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                <Card sx={{ border: 1, borderColor: "primary.light", bgcolor: "primary.main" + "08" }}>
                  <CardContent sx={{ display: "flex", flexDirection: "column", alignItems: "center", py: 5 }}>
                    <CurrencyRupeeIcon color="primary" sx={{ fontSize: 40, mb: 1 }} />
                    <Typography variant="body2" color="text.secondary">Estimated Rate</Typography>
                    <Typography variant="h3" fontWeight={700}>₹{result.rate.toFixed(2)}</Typography>
                  </CardContent>
                </Card>

                {result.slab && (
                  <Card>
                    <CardContent>
                      <Typography variant="subtitle2" gutterBottom>Matched Slab Details</Typography>
                      <Divider sx={{ mb: 1.5 }} />
                      {[
                        ["Weight Range", `${result.slab.fromWeight} – ${result.slab.toWeight} kg`],
                        ["Rate per kg", `₹${result.slab.ratePerKg}`],
                        ["Min Charge", `₹${result.slab.minCharge}`],
                        ["Weight Entered", `${weight} kg`],
                      ].map(([label, val]) => (
                        <Box key={label} sx={{ display: "flex", justifyContent: "space-between", py: 0.5 }}>
                          <Typography variant="body2" color="text.secondary">{label}</Typography>
                          <Typography variant="body2" fontWeight={500}>{val}</Typography>
                        </Box>
                      ))}
                    </CardContent>
                  </Card>
                )}
              </Box>
            ) : (
              <Paper sx={{ display: "flex", alignItems: "center", justifyContent: "center", minHeight: 300, color: "text.secondary" }}>
                <Box sx={{ textAlign: "center" }}>
                  <LocalShippingIcon sx={{ fontSize: 48, opacity: 0.3, mb: 1 }} />
                  <Typography variant="body2">Fill in shipment details and click Calculate</Typography>
                </Box>
              </Paper>
            )}
          </Grid>
        </Grid>
      </Box>
    </Box>
  );
};

const RatecardListWithProvider = () => (
  <CourierProvider>
    <RatecardList />
  </CourierProvider>
);

export default RatecardListWithProvider;
